import { FollowUpItem } from "../models/FollowUp";
import { ThemeStatisticsItem, ThemeStatisticsQuery, ThemeStatisticsReport } from "../models/ThemeStatistics";
import { UPCARE_THEMES } from "../utils/UpCareThemes";
import { ThemeStatisticsEvaluator } from "./ThemeStatisticsEvaluator";

/**
 * POC implementation of the future FHIR Measure evaluation.
 * It intentionally exposes the same report contract that a future
 * $evaluate-measure adapter will populate from a MeasureReport.
 *
 * For themes, undefined means "all themes" while [] means "no theme".
 */
class FrontThemeStatisticsEvaluator implements ThemeStatisticsEvaluator {
    constructor(private readonly items: FollowUpItem[]) {}

    async evaluate(query: ThemeStatisticsQuery): Promise<ThemeStatisticsReport> {
        const filtered = this.items.filter((item) => this.matches(item, query));
        const themes = UPCARE_THEMES
            .filter((theme) => query.themes === undefined || query.themes.includes(theme.code))
            .map((theme): ThemeStatisticsItem => {
                const themedItems = filtered.filter((item) => item.themeCode === theme.code);
                return {
                    theme: theme.code,
                    label: theme.display,
                    total: themedItems.length,
                    requests: themedItems.filter((item) => item.status === "request").length,
                    proposals: themedItems.filter((item) => item.status === "proposal").length,
                    scheduled: themedItems.filter((item) => item.status === "scheduled").length,
                    completed: themedItems.filter((item) => item.status === "completed").length
                };
            });

        const total = themes.reduce((sum, theme) => sum + theme.total, 0);
        const dominant = [...themes].sort((left, right) => right.total - left.total)[0];

        return {
            period: query.periodStart || query.periodEnd
                ? { start: query.periodStart, end: query.periodEnd }
                : undefined,
            total,
            activeThemeCount: themes.filter((theme) => theme.total > 0).length,
            dominantTheme: dominant?.total ? dominant.theme : undefined,
            themes,
            source: "front"
        };
    }

    private matches(item: FollowUpItem, query: ThemeStatisticsQuery): boolean {
        if (query.values?.length && !query.values.some((value) => item.values.includes(value))) return false;
        if (query.themes !== undefined) {
            if (query.themes.length === 0) return false;
            if (!item.themeCode || !query.themes.includes(item.themeCode)) return false;
        }
        if (query.natures?.length && !query.natures.includes(item.nature)) return false;
        if (query.organizations?.length && !query.organizations.includes(item.carriedBy)) return false;
        if (query.statuses?.length && !query.statuses.includes(item.status)) return false;

        if (query.periodStart || query.periodEnd) {
            const candidate = item.date ?? item.publishedAt;
            if (!candidate) return false;
            const timestamp = new Date(candidate).getTime();
            if (!Number.isFinite(timestamp)) return false;
            if (query.periodStart && timestamp < new Date(query.periodStart).getTime()) return false;
            if (query.periodEnd && timestamp > new Date(query.periodEnd).getTime()) return false;
        }

        return true;
    }
}

export default FrontThemeStatisticsEvaluator;
